import { useEffect, useRef } from "react";

const MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function RegistryMascot() {
  const sceneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || window.matchMedia(MOTION_QUERY).matches) return;

    let animationFrame = 0;
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;

    const renderPosition = () => {
      currentX += (targetX - currentX) * 0.16;
      currentY += (targetY - currentY) * 0.16;
      scene.style.setProperty("--mascot-look-x", `${(currentX * 9).toFixed(2)}px`);
      scene.style.setProperty("--mascot-look-y", `${(currentY * 7).toFixed(2)}px`);
      scene.style.setProperty("--mascot-shift-x", `${(currentX * 12).toFixed(2)}px`);
      scene.style.setProperty("--mascot-shift-y", `${(currentY * 8).toFixed(2)}px`);
      scene.style.setProperty("--mascot-tilt", `${(currentX * 2.4).toFixed(2)}deg`);
      scene.style.setProperty("--mascot-parallax-x", `${(currentX * -18).toFixed(2)}px`);
      scene.style.setProperty("--mascot-parallax-y", `${(currentY * -12).toFixed(2)}px`);

      if (Math.abs(targetX - currentX) + Math.abs(targetY - currentY) > 0.002) {
        animationFrame = window.requestAnimationFrame(renderPosition);
      } else {
        animationFrame = 0;
      }
    };

    const requestRender = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(renderPosition);
    };

    const handlePointerMove = (event: PointerEvent) => {
      const bounds = scene.getBoundingClientRect();
      const centerX = bounds.left + bounds.width / 2;
      const centerY = bounds.top + bounds.height / 2;
      targetX = clamp((event.clientX - centerX) / (window.innerWidth * 0.45), -1, 1);
      targetY = clamp((event.clientY - centerY) / (window.innerHeight * 0.45), -1, 1);
      requestRender();
    };

    const resetPosition = () => {
      targetX = 0;
      targetY = 0;
      requestRender();
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    window.addEventListener("blur", resetPosition);
    document.documentElement.addEventListener("mouseleave", resetPosition);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("blur", resetPosition);
      document.documentElement.removeEventListener("mouseleave", resetPosition);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div
      ref={sceneRef}
      className="registry-mascot-scene"
      role="img"
      aria-label="会跟随鼠标视线移动的档案守护员吉祥物"
    >
      <span className="mascot-orbit mascot-orbit--outer" aria-hidden="true" />
      <span className="mascot-orbit mascot-orbit--inner" aria-hidden="true" />
      <span className="mascot-spark mascot-spark--one" aria-hidden="true" />
      <span className="mascot-spark mascot-spark--two" aria-hidden="true" />
      <span className="mascot-spark mascot-spark--three" aria-hidden="true" />

      <svg className="registry-mascot" viewBox="0 0 620 560" aria-hidden="true">
        <defs>
          <linearGradient id="mascot-shell" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#84d9ff" />
            <stop offset="0.48" stopColor="#4c8dff" />
            <stop offset="1" stopColor="#2752d6" />
          </linearGradient>
          <linearGradient id="mascot-face" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#152957" />
            <stop offset="1" stopColor="#091633" />
          </linearGradient>
          <linearGradient id="mascot-paper" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#dce9ff" />
          </linearGradient>
          <filter id="mascot-shadow" x="-40%" y="-40%" width="180%" height="190%">
            <feDropShadow dx="0" dy="22" stdDeviation="20" floodColor="#061847" floodOpacity=".3" />
          </filter>
          <filter id="mascot-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="9" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <ellipse className="mascot-ground" cx="310" cy="508" rx="150" ry="28" />

        <g className="mascot-card mascot-card--left">
          <rect x="67" y="177" width="96" height="122" rx="18" />
          <path d="M91 211h49M91 230h36M91 249h43" />
          <circle cx="134" cy="274" r="11" />
          <path d="m129 274 4 4 7-9" />
        </g>
        <g className="mascot-card mascot-card--right">
          <rect x="467" y="140" width="86" height="104" rx="18" />
          <path d="M490 174h39M490 193h28M490 212h34" />
        </g>

        <g className="mascot-float">
          <g className="mascot-body" filter="url(#mascot-shadow)">
            <path
              className="mascot-ear mascot-ear--left"
              d="M177 237c-25 0-45 20-45 45s20 45 45 45h18v-90h-18Z"
            />
            <path
              className="mascot-ear mascot-ear--right"
              d="M443 237c25 0 45 20 45 45s-20 45-45 45h-18v-90h18Z"
            />

            <path
              className="mascot-shell"
              d="M205 181h82l27-34 27 34h74c30 0 54 24 54 54v134c0 55-45 100-100 100H251c-55 0-100-45-100-100V235c0-30 24-54 54-54Z"
            />
            <path
              className="mascot-shell-highlight"
              d="M204 202h77l29-35 29 35h72c17 0 31 14 31 31"
            />

            <rect className="mascot-screen" x="190" y="218" width="240" height="145" rx="52" />
            <path className="mascot-screen-shine" d="M226 238c41-17 111-19 168 4" />

            <g className="mascot-eye mascot-eye--left">
              <rect x="228" y="258" width="66" height="54" rx="27" />
              <circle className="mascot-pupil" cx="261" cy="285" r="13" />
              <circle className="mascot-eye-glint" cx="266" cy="279" r="4" />
            </g>
            <g className="mascot-eye mascot-eye--right">
              <rect x="326" y="258" width="66" height="54" rx="27" />
              <circle className="mascot-pupil" cx="359" cy="285" r="13" />
              <circle className="mascot-eye-glint" cx="364" cy="279" r="4" />
            </g>
            <path className="mascot-mouth" d="M282 329c17 14 39 14 56 0" />

            <path
              className="mascot-arm mascot-arm--left"
              d="M177 365c-36 9-53 37-46 66 5 19 20 30 43 31"
            />
            <path
              className="mascot-arm mascot-arm--right"
              d="M443 365c36 9 53 37 46 66-5 19-20 30-43 31"
            />

            <g className="mascot-document">
              <path d="M234 373h118l38 38v91H234z" />
              <path className="mascot-document-fold" d="M352 373v38h38" />
              <path className="mascot-document-line" d="M265 412h54M265 434h83M265 456h61" />
              <circle className="mascot-document-seal" cx="351" cy="465" r="18" />
              <path className="mascot-document-check" d="m342 465 7 7 13-16" />
            </g>

            <circle className="mascot-hand" cx="224" cy="435" r="23" />
            <circle className="mascot-hand" cx="396" cy="435" r="23" />
          </g>

          <g className="mascot-antenna">
            <path d="M310 149v-27" />
            <circle cx="310" cy="105" r="16" filter="url(#mascot-glow)" />
          </g>
        </g>
      </svg>
    </div>
  );
}
